import { Check, Settings2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui';
import { usePreferences } from '@/hooks/use-preferences';
import { CHART_STYLES } from '@/lib/preferences';
import { cn } from '@/lib/utils';

/**
 * The chart's own settings, next to its header. A small menu rather than a dialog: there is
 * one choice to make, it applies at once, and the reader wants to see the chart change
 * behind it. The choice is a preference, so it is saved and holds on the next visit.
 */
export function ChartSettingsMenu() {
  const { preferences, setPreferences } = usePreferences();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target) !== true) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      <Button
        variant="ghost"
        className="size-7 px-0"
        aria-label="Chart settings"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Chart settings"
        onClick={() => setOpen((value) => !value)}
      >
        <Settings2 className="size-3.5" />
      </Button>
      {open && (
        <div role="menu" aria-label="Chart style" className="absolute top-full right-0 z-20 mt-1 w-40 rounded-md border border-line bg-surface p-1 shadow-lg">
          <div className="px-2 pt-1 pb-1.5 text-[11px] font-semibold tracking-wide text-ink-2 uppercase">Chart style</div>
          {CHART_STYLES.map((option) => {
            const selected = preferences.chartStyle === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  setPreferences({ ...preferences, chartStyle: option.value });
                  setOpen(false);
                }}
                className={cn('flex h-7 w-full items-center gap-2 rounded-sm px-2 text-left text-xs', selected ? 'text-ink' : 'text-ink-2 hover:bg-surface-2 hover:text-ink')}
              >
                <Check className={cn('size-3.5', selected ? 'text-accent' : 'invisible')} />
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
